import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Badge, FadeIn, Sheet, FullScreen, Field, Portal, useIsDesktop } from '../components/ui';
import { rupee } from '../theme';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { pickImage } from '../api/imagePicker';
import { saveNodeAsImage, shareText } from '../utils/download';
import IdCard from '../components/IdCard';
import ServiceHistory from './ServiceHistory';
import BecomePartner from './BecomePartner';
import MlmDashboard from './MlmDashboard';
import api from '../api/client';

const statusTone = { in_progress: 'brand', completed: 'success', assigned: 'warning', cancelled: 'neutral' };

const BpStat = ({ label, value }) => (
  <div style={{ flex: 1, textAlign: 'center' }}>
    <div style={{ fontSize: 15.5, fontWeight: 900 }}>{value}</div>
    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{label}</div>
  </div>
);

const HistRow = ({ title, sub, right }) => (
  <div className="row" style={{ gap: 10, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
    <div className="grow">
      <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{sub}</div>
    </div>
    {right}
  </div>
);

export default function Profile() {
  const { user, logout, setUser, refreshUser } = useAuth();
  const { toast } = useToast();
  const isDesktop = useIsDesktop();
  const isStaff = user?.role === 'staff';
  const locked = isStaff && user?.kyc?.status === 'approved'; // name locked to KYC

  const [sheet, setSheet] = useState(null); // 'edit' | 'work' | 'salary' | 'bank' | 'support' | 'history'
  const [showCard, setShowCard] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [earn, setEarn] = useState({ workHistory: [], salaryHistory: [] });
  const [mlm, setMlm] = useState(null);
  const [referral, setReferral] = useState(null);
  const [becomeOpen, setBecomeOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const cardRef = useRef(null);

  const loadMlm = () => api.get('/mlm/me').then(setMlm).catch(() => setMlm(null));
  const loadReferral = () => api.get('/users/referral').then(setReferral).catch(() => setReferral(null));

  const ex = user?.kyc?.extracted || {};
  const avatarUri = user?.profilePhoto || user?.kyc?.selfieImage;

  // edit form
  const [name, setName] = useState(user?.name || '');
  const [photo, setPhoto] = useState(user?.profilePhoto || null);
  const [age, setAge] = useState(user?.age || '');
  const [homeTown, setHomeTown] = useState(user?.homeTown || '');
  const [experienceYears, setExperienceYears] = useState(user?.experienceYears ? String(user.experienceYears) : '');
  const [skills, setSkills] = useState((user?.skills || []).join(', '));
  // support form
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [tickets, setTickets] = useState([]);
  const [bank, setBank] = useState(user?.bankDetails || { accountName: '', accountNumber: '', ifsc: '', bankName: '', upi: '' });

  useEffect(() => {
    if (isStaff) {
      api.get('/bookings/earnings').then((d) => d && setEarn(d)).catch(() => {});
      loadMlm();
    } else {
      loadReferral();
    }
  }, [isStaff]);

  const downloadCard = async () => {
    setSaving(true);
    const res = await saveNodeAsImage(cardRef.current, `${user?.staffId || 'narayan'}-id-card.png`);
    setSaving(false);
    if (res.ok) toast('Your ID card image has been downloaded.', { title: 'Saved', tone: 'success' });
    else toast(res.error || 'Please try again.', { title: 'Could not save', tone: 'error' });
  };

  const doShare = async (text) => {
    const r = await shareText(text);
    if (r.copied) toast('Copied to clipboard', { tone: 'success' });
    else if (!r.ok) toast(r.error, { tone: 'error' });
  };

  const saveProfile = async () => {
    // Verified staff can only change their photo; name is locked to KYC.
    const payload = locked ? { profilePhoto: photo } : { name: name.trim(), profilePhoto: photo };
    if (isStaff) {
      payload.age = String(age).trim();
      payload.homeTown = homeTown.trim();
      payload.experienceYears = Number(experienceYears) || 0;
      payload.skills = skills.split(',').map((s) => s.trim()).filter(Boolean);
    }
    try {
      const d = await api.put('/users/me', payload);
      if (d?.user) setUser(d.user); else refreshUser();
      toast('Profile updated', { tone: 'success' });
    } catch (e) {
      setUser({ ...user, ...payload }); // offline: update locally
    }
    setSheet(null);
  };

  const saveBank = async () => {
    try {
      const d = await api.post('/users/me/bank', bank);
      if (d?.bankDetails) setUser({ ...user, bankDetails: d.bankDetails });
      toast('Bank details saved', { tone: 'success' });
    } catch (e) {
      setUser({ ...user, bankDetails: bank });
    }
    setSheet(null);
  };

  const openSupport = async () => {
    setSheet('support');
    try {
      const d = await api.get('/tickets');
      if (d?.tickets) setTickets(d.tickets);
    } catch (e) { /* offline */ }
  };

  const submitTicket = async () => {
    if (!subject.trim()) return toast('Enter a subject', { tone: 'error' });
    try {
      await api.post('/tickets', { subject: subject.trim(), message: message.trim(), category: 'assistance' });
    } catch (e) { /* offline */ }
    toast('Our support team will get back to you.', { title: 'Ticket raised', tone: 'success' });
    setSubject('');
    setMessage('');
    setSheet(null);
  };

  const menu = isStaff
    ? [
        { key: 'edit', icon: '✏️', label: 'Edit Profile', onClick: () => setSheet('edit') },
        { key: 'work', icon: '🗂️', label: 'Work History', onClick: () => setSheet('work') },
        { key: 'salary', icon: '💰', label: 'Payment History', onClick: () => setSheet('salary') },
        { key: 'bank', icon: '🏦', label: 'Bank Details', onClick: () => setSheet('bank') },
        { key: 'support', icon: '🎧', label: 'Support Tickets', onClick: openSupport },
      ]
    : [
        { key: 'edit', icon: '✏️', label: 'Edit Profile', onClick: () => setSheet('edit') },
        { key: 'history', icon: '🧾', label: 'Service History', onClick: () => setShowHistory(true) },
        { key: 'support', icon: '🎧', label: 'Support Tickets', onClick: openSupport },
      ];

  return (
    <div className="screen">
      <div className={isDesktop ? 'profile-grid' : ''}>
      <div className={isDesktop ? 'profile-grid__aside' : ''}>

      <div className="header-red" style={{ textAlign: 'center' }}>
        <FadeIn>
          <span className="avatar" style={{
            width: 84, height: 84, margin: '0 auto', fontSize: 28,
            background: 'rgba(255,255,255,0.2)', border: '3px solid #fff',
          }}>
            {avatarUri ? <img src={avatarUri} alt="" /> : (user?.name || user?.mobile || 'U').charAt(0).toUpperCase()}
          </span>
          <div style={{ fontSize: 21, fontWeight: 800, marginTop: 12 }}>{user?.name || 'Welcome'}</div>
          <div style={{ fontSize: 13.5, marginTop: 2, color: 'rgba(255,255,255,0.85)' }}>+91 {user?.mobile}</div>
          <div style={{ marginTop: 8 }}>
            <Badge label={isStaff ? '🧰 Service Provider' : '👤 Customer'} tone="success" />
          </div>
        </FadeIn>
      </div>

      {/* Show ID Card (staff) */}
      {isStaff && (
        <FadeIn delay={60} style={{ padding: '16px 16px 0' }}>
          <button className="row" onClick={() => setShowCard(true)} style={{
            width: '100%', textAlign: 'left', gap: 14, background: 'var(--primary)',
            borderRadius: 'var(--r-lg)', padding: 16, boxShadow: 'var(--shadow-card)',
          }}>
            <span style={{
              width: 46, height: 46, borderRadius: 12, background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flex: 'none',
            }}>🪪</span>
            <span className="grow">
              <span style={{ display: 'block', color: '#fff', fontSize: 16, fontWeight: 800 }}>Show My ID Card</span>
              <span style={{ display: 'block', color: 'rgba(255,255,255,0.85)', fontSize: 12.5, marginTop: 2 }}>
                {user?.staffId || 'NRN-—'} · Tap to view &amp; download
              </span>
            </span>
            <span style={{ color: '#fff', fontSize: 20 }}>›</span>
          </button>
        </FadeIn>
      )}

      {/* Rating */}
      {isStaff && user?.rating > 0 && (
        <FadeIn delay={90} style={{ padding: '14px 16px 0' }}>
          <Card className="row" style={{ gap: 14 }}>
            <span style={{
              width: 56, height: 56, borderRadius: 14, background: 'var(--primary-soft)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, fontWeight: 800, color: 'var(--primary)', flex: 'none',
            }}>{user.rating}</span>
            <div className="grow">
              <div style={{ fontSize: 15, fontWeight: 800 }}>{'⭐'.repeat(Math.round(user.rating))}</div>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>Your service rating</div>
            </div>
          </Card>
        </FadeIn>
      )}

      {/* Verified identity (read-only) */}
      {isStaff && locked && (
        <FadeIn delay={120} style={{ padding: '14px 16px 0' }}>
          <Card>
            <div className="row-between" style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 800 }}>Verified Identity 🔒</span>
              <Badge label="Auto-filled" tone="success" />
            </div>
            {[
              ['PAN', ex.pan],
              ['Aadhaar', ex.aadhaarMasked],
              ['Date of birth', ex.dob],
              ['Gender', ex.gender],
              ['Address', [ex.address?.building, ex.address?.street, ex.address?.locality, ex.address?.city, ex.address?.state, ex.address?.pincode].filter(Boolean).join(', ')],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="row-between" style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
                <span className="muted" style={{ fontSize: 13 }}>{k}</span>
                <span style={{ fontSize: 13.5, fontWeight: 700, flex: 1, textAlign: 'right' }}>{v}</span>
              </div>
            ))}
            <p className="faint" style={{ fontSize: 11.5, marginTop: 10, marginBottom: 0 }}>
              These details are from your verified documents and can't be edited.
            </p>
          </Card>
        </FadeIn>
      )}

      </div>
      <div className={isDesktop ? 'profile-grid__main' : ''}>

      {/* Business Partner (MLM) */}
      {isStaff && mlm?.enabled && (
        <FadeIn delay={100} style={{ padding: '14px 16px 0' }}>
          {mlm.isPartner ? (
            <Card style={{ border: '1px solid var(--primary-soft)' }}>
              <div className="row" style={{ marginBottom: 12 }}>
                <span style={{ fontSize: 20 }}>🤝</span>
                <span style={{ fontSize: 15.5, fontWeight: 800 }}>Business Partner</span>
                <span className="grow" />
                <Badge label="Active" tone="success" />
              </div>
              <div className="row-between" style={{ background: 'var(--primary-soft)', borderRadius: 'var(--r-md)', padding: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 700 }}>Referral code</div>
                  <div style={{ fontSize: 19, fontWeight: 900, color: 'var(--primary)', letterSpacing: 2, marginTop: 2 }}>{mlm.referralCode}</div>
                </div>
                <button className="btn btn--primary btn--sm"
                  onClick={() => doShare(`Join my Narayan team with referral code: ${mlm.referralCode}`)}>
                  Share
                </button>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <BpStat label="Per join" value={rupee(mlm.perJoinAmount)} />
                <BpStat label="Joined" value={mlm.teamCount} />
                <BpStat label="Earned" value={rupee(mlm.earned)} />
              </div>
              {mlm.pending > 0 && (
                <div style={{ fontSize: 12, color: 'var(--warning)', fontWeight: 700, marginTop: 10 }}>
                  {rupee(mlm.pending)} pending admin approval
                </div>
              )}
              <Button title="Open Partner Dashboard →" onClick={() => setDashOpen(true)} style={{ marginTop: 12 }} />
            </Card>
          ) : (
            <Card style={{ border: '1px solid var(--primary-soft)' }}>
              <div style={{ fontSize: 15.5, fontWeight: 800 }}>🤝 Become a Partner</div>
              <p className="muted" style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.45 }}>
                Build your own team and earn {rupee(mlm.perJoinAmount)} on every join. Payouts released after admin approval.
              </p>
              <Button title="Join as Partner" onClick={() => setBecomeOpen(true)} style={{ marginTop: 12 }} />
            </Card>
          )}
        </FadeIn>
      )}

      {/* Refer & Earn (customers) */}
      {!isStaff && referral?.code && (
        <FadeIn delay={100} style={{ padding: '14px 16px 0' }}>
          <Card style={{ border: '1px solid var(--primary-soft)' }}>
            <div className="row" style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 20 }}>🎁</span>
              <span style={{ fontSize: 15.5, fontWeight: 800 }}>{referral.enabled ? 'Refer & Earn' : 'Invite Friends'}</span>
            </div>
            <p className="muted" style={{ fontSize: 12.5, marginTop: 0, lineHeight: 1.45 }}>
              {referral.enabled
                ? `Invite friends — earn ${rupee(referral.amount)} when they complete their first booking.`
                : 'Share your code and invite friends to Narayan.'}
            </p>
            <div className="row-between" style={{ background: 'var(--primary-soft)', borderRadius: 'var(--r-md)', padding: 12, marginTop: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 700 }}>Your referral code</div>
                <div style={{ fontSize: 19, fontWeight: 900, color: 'var(--primary)', letterSpacing: 2, marginTop: 2 }}>{referral.code}</div>
              </div>
              <button className="btn btn--primary btn--sm"
                onClick={() => doShare(`Book trusted home help on Narayan and use my referral code ${referral.code}!`)}>
                Share
              </button>
            </div>
            {referral.enabled && (
              <div className="row" style={{ gap: 8 }}>
                <BpStat label="Invited" value={referral.referredCount} />
                <BpStat label="Earned" value={rupee(referral.earned)} />
                <BpStat label="Pending" value={rupee(referral.pending)} />
              </div>
            )}
          </Card>
        </FadeIn>
      )}

      {/* Menu */}
      <div className={isDesktop ? 'profile-menu' : 'stack'} style={{ padding: isDesktop ? 0 : 16, gap: 12 }}>
        {menu.map((m, i) => (
          <FadeIn key={m.key} delay={i * 50}>
            <Card className="row" style={{ gap: 14, padding: '16px 18px' }} onClick={m.onClick}>
              <span style={{ fontSize: 20 }}>{m.icon}</span>
              <span className="grow" style={{ fontSize: 14.5, fontWeight: 600 }}>{m.label}</span>
              <span className="faint" style={{ fontSize: 18 }}>›</span>
            </Card>
          </FadeIn>
        ))}
      </div>
      {/* On desktop the sidebar already owns Logout. */}
      {!isDesktop && (
        <div style={{ padding: '0 16px 16px' }}>
          <Button variant="ghost" title="Logout" onClick={logout} />
        </div>
      )}

      </div>
      </div>

      {/* ---------- Sheets ---------- */}

      <Sheet open={sheet === 'edit'} onClose={() => setSheet(null)} title="Edit Profile">
        <button className="photo-picker"
          onClick={async () => { const r = await pickImage(); if (r?.dataUri) setPhoto(r.dataUri); }}>
          <span className="photo-picker__ring">
            {photo
              ? <img src={photo} alt="" />
              : <span className="photo-picker__ph">{(user?.name || 'U').charAt(0).toUpperCase()}</span>}
            <span className="photo-picker__badge">📷</span>
          </span>
          <span className="photo-picker__label">{photo ? 'Change photo' : 'Add a photo'}</span>
        </button>

        <Field
          label={`Full name ${locked ? '🔒' : ''}`} value={name} disabled={locked}
          onChange={(e) => setName(e.target.value)} placeholder="Your name"
          hint={locked ? 'Your name & address are locked to your verified KYC. You can still complete the details below.' : undefined}
        />

        {isStaff && (
          <>
            <Field label="Speciality (comma separated)" value={skills}
              onChange={(e) => setSkills(e.target.value)} placeholder="Cooking, Cleaning, Babysitting" />
            <div className="field-row">
              <Field label="Years of experience" value={experienceYears} inputMode="numeric" placeholder="e.g. 5"
                onChange={(e) => setExperienceYears(e.target.value.replace(/\D/g, ''))} />
              <Field label="Age" value={age} inputMode="numeric" placeholder="e.g. 30"
                onChange={(e) => setAge(e.target.value.replace(/\D/g, ''))} />
            </div>
            <Field label="Home town" value={homeTown} onChange={(e) => setHomeTown(e.target.value)} placeholder="e.g. Jaipur, Rajasthan" />
          </>
        )}

        <Button title="Save changes" onClick={saveProfile} style={{ marginTop: 18 }} />
      </Sheet>

      <Sheet open={sheet === 'work'} onClose={() => setSheet(null)} title="Work History">
        {(earn.workHistory || []).length === 0 ? (
          <p className="muted center" style={{ padding: '30px 0' }}>No work history yet.</p>
        ) : earn.workHistory.map((w, i) => (
          <HistRow key={i} title={w.title} sub={`${w.customer || '—'} · ${w.bookingCode}`}
            right={
              <div style={{ textAlign: 'right' }}>
                <Badge label={(w.status || '').replace('_', ' ')} tone={statusTone[w.status] || 'neutral'} />
                <div style={{ fontSize: 14, fontWeight: 800, marginTop: 4 }}>{rupee(w.staffPay)}/mo</div>
              </div>
            } />
        ))}
      </Sheet>

      <Sheet open={sheet === 'salary'} onClose={() => setSheet(null)} title="Salary Credited History">
        {(earn.salaryHistory || []).length === 0 ? (
          <p className="muted center" style={{ padding: '30px 0' }}>No salary credited yet.</p>
        ) : earn.salaryHistory.map((s, i) => (
          <HistRow key={i} title={s.title} sub={`${s.month} · ${s.bookingCode}`}
            right={<span style={{ fontSize: 14, fontWeight: 800, color: 'var(--success)' }}>+{rupee(s.amount)}</span>} />
        ))}
      </Sheet>

      <Sheet open={sheet === 'bank'} onClose={() => setSheet(null)} title="Bank Details">
        <p className="muted" style={{ fontSize: 12.5, marginBottom: 14, marginTop: 0 }}>Used by admin to settle your payouts.</p>
        {[
          ['accountName', 'Account holder name'],
          ['accountNumber', 'Account number'],
          ['ifsc', 'IFSC code'],
          ['bankName', 'Bank name'],
          ['upi', 'UPI ID (optional)'],
        ].map(([key, label]) => (
          <Field key={key} label={label} placeholder={label} value={bank[key]}
            inputMode={key === 'accountNumber' ? 'numeric' : undefined}
            onChange={(e) => setBank({ ...bank, [key]: key === 'ifsc' ? e.target.value.toUpperCase() : e.target.value })} />
        ))}
        <Button title="Save Bank Details" onClick={saveBank} style={{ marginTop: 8 }} />
      </Sheet>

      <Sheet open={sheet === 'support'} onClose={() => setSheet(null)} title="Support Tickets">
        <Field label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Payment not received" />
        <Field as="textarea" label="Message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe your issue…" />
        <Button title="Raise Ticket" onClick={submitTicket} style={{ marginTop: 16 }} />

        {tickets.length > 0 && (
          <>
            <div className="field__label" style={{ marginTop: 20 }}>Your tickets</div>
            {tickets.map((t) => (
              <HistRow key={t._id} title={t.subject} sub={t.ticketCode}
                right={<Badge label={(t.status || 'open').replace('_', ' ')} tone={t.status === 'resolved' ? 'success' : 'warning'} />} />
            ))}
          </>
        )}
      </Sheet>

      {/* ---------- Full-screen overlays ---------- */}

      <FullScreen open={showHistory} onClose={() => setShowHistory(false)}>
        <ServiceHistory onClose={() => setShowHistory(false)} />
      </FullScreen>

      {showCard && (
        <Portal>
        <div className="modal-backdrop modal-backdrop--center"
          style={{ background: 'rgba(20,10,12,0.9)', overflowY: 'auto' }}
          onClick={() => setShowCard(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ display: 'inline-block' }}><IdCard user={user} cardRef={cardRef} /></div>
            <div className="btn-row" style={{ marginTop: 24, justifyContent: 'center' }}>
              <Button variant="glass" title="Close" onClick={() => setShowCard(false)} />
              <Button variant="light" title={saving ? 'Saving…' : '⬇  Download'} onClick={downloadCard} disabled={saving} />
            </div>
          </div>
        </div>
        </Portal>
      )}

      <FullScreen open={becomeOpen} onClose={() => { setBecomeOpen(false); loadMlm(); }}>
        <BecomePartner initialSponsor={mlm?.pendingSponsorCode} fields={mlm?.formFields}
          onClose={() => { setBecomeOpen(false); loadMlm(); }} />
      </FullScreen>

      <FullScreen open={dashOpen} onClose={() => setDashOpen(false)}>
        {mlm?.isPartner && (
          <MlmDashboard onClose={() => setDashOpen(false)} referralCode={mlm.referralCode} perJoinAmount={mlm.perJoinAmount} />
        )}
      </FullScreen>
    </div>
  );
}
